/**
 * Maestros · Eventos ANMAT — catálogo del SNT con gestión de defaults.
 *
 * Lectura: cualquier usuario ve el catálogo completo + qué evento es el
 * default actual para cada tupla (informador, origen, destino).
 *
 * Edición (paquete O): Admin con permiso `editar_eventos_default` puede:
 *   - "Marcar como default": setea este evento como custom para su tupla.
 *   - "Quitar custom": vuelve al hardcoded (`DISTRIBUCION DEL PRODUCTO...`
 *     o `RECEPCION DE PRODUCTO...`).
 *
 * Persiste en `useEventosDefaultStore` (localStorage). Audit cada cambio.
 *
 * Filtros: informador, origen, destino, búsqueda de texto, "solo defaults".
 */

import { useMemo, useState } from 'react';
import { Star, StarOff, Filter, Search } from 'lucide-react';
import { toast } from '@/lib/notify';
import { useEventosAnmat } from '@/api/queries';
import { useEventosDefaultStore } from '@/stores/eventos-default';
import { useSessionStore } from '@/stores/session';
import { useAuditStore } from '@/stores/audit';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { esEventoDefaultActual, eventoAnmatDefaultKey } from '@/domain/anmat';
import { puedeActuar } from '@/domain/rbac';
import { TIPOS_ACTOR } from '@/domain/types';
import type { EventoAnmat, TipoActor } from '@/domain/types';

export function EventosTab() {
  const { data: eventos = [] } = useEventosAnmat();
  const defaults = useEventosDefaultStore((s) => s.defaults);
  const setDefault = useEventosDefaultStore((s) => s.setDefault);
  const removeDefault = useEventosDefaultStore((s) => s.removeDefault);
  const usuario = useSessionStore((s) => s.usuario);
  const addAuditEntry = useAuditStore((s) => s.addEntry);

  const puedeEditar = puedeActuar(usuario, 'editar_eventos_default');

  const [busqueda, setBusqueda] = useState('');
  const [filtroInformador, setFiltroInformador] = useState<string>('all');
  const [filtroOrigen, setFiltroOrigen] = useState<string>('all');
  const [filtroDestino, setFiltroDestino] = useState<string>('all');
  const [soloDefaults, setSoloDefaults] = useState(false);

  // Aplicar filtros
  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return eventos.filter((e) => {
      if (filtroInformador !== 'all' && e.informador !== filtroInformador) return false;
      if (filtroOrigen !== 'all' && e.origen !== filtroOrigen) return false;
      if (filtroDestino !== 'all') {
        if (filtroDestino === '__null' && e.destino !== null) return false;
        if (filtroDestino !== '__null' && e.destino !== filtroDestino) return false;
      }
      if (soloDefaults && !esEventoDefaultActual(e, defaults)) return false;
      if (q && !e.evento.toLowerCase().includes(q) && !String(e.id).includes(q)) {
        return false;
      }
      return true;
    });
  }, [eventos, busqueda, filtroInformador, filtroOrigen, filtroDestino, soloDefaults, defaults]);

  // Conteos para resumen
  const conteoCustom = Object.keys(defaults).length;

  const handleSetDefault = (e: EventoAnmat) => {
    if (!puedeEditar) {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'permiso.denegado',
        detalle: 'Editar evento default sin permiso',
      });
      toast.error('Sin permiso · solo Administrador');
      return;
    }
    const claveAntes = eventoAnmatDefaultKey(e.informador, e.origen, e.destino);
    const customAntes = defaults[claveAntes];
    setDefault(e.informador, e.origen, e.destino, e.id);
    addAuditEntry({
      categoria: 'configuracion',
      accion: 'evento_anmat.set_default',
      entidadTipo: 'evento_anmat',
      entidadId: String(e.id),
      valoresAntes: {
        clave: claveAntes,
        idCustomAnterior: customAntes ?? null,
      },
      valoresDespues: {
        clave: claveAntes,
        idCustomNuevo: e.id,
        eventoTexto: e.evento,
      },
      razon: `Default custom para ${e.informador} · ${e.origen} → ${e.destino ?? '— (terminal)'}: evento #${e.id} "${e.evento}"`,
      significadoFirma: 'Cambio de configuración del catálogo ANMAT',
    });
    toast.success(`Evento #${e.id} marcado como default para esta tupla`);
  };

  const handleRemoveDefault = (e: EventoAnmat) => {
    if (!puedeEditar) {
      addAuditEntry({
        categoria: 'sesion',
        accion: 'permiso.denegado',
        detalle: 'Quitar evento default sin permiso',
      });
      toast.error('Sin permiso · solo Administrador');
      return;
    }
    const clave = eventoAnmatDefaultKey(e.informador, e.origen, e.destino);
    const idCustom = defaults[clave];
    if (idCustom === undefined) {
      toast.error('Esta tupla no tiene custom — ya usa el default hardcoded');
      return;
    }
    removeDefault(e.informador, e.origen, e.destino);
    addAuditEntry({
      categoria: 'configuracion',
      accion: 'evento_anmat.quitar_default',
      entidadTipo: 'evento_anmat',
      entidadId: String(idCustom),
      valoresAntes: { clave, idCustomAnterior: idCustom },
      valoresDespues: { clave, idCustomNuevo: null, fallback: 'hardcoded' },
      razon: `Custom removido para ${e.informador} · ${e.origen} → ${e.destino ?? '— (terminal)'} (vuelve al hardcoded)`,
      significadoFirma: 'Cambio de configuración del catálogo ANMAT',
    });
    toast.success(`Custom removido · vuelve al default hardcoded del sistema`);
  };

  return (
    <Card>
      <CardContent className="space-y-4 pt-4">
        {/* Banner explicativo */}
        <Alert className="border-blue-200 bg-blue-50">
          <Filter className="h-4 w-4 text-blue-700" />
          <AlertDescription className="text-xs text-blue-900">
            <b>Catálogo SNT</b> · {eventos.length} eventos. La columna <b>Default</b> muestra
            el evento que el sistema infiere por defecto para cada tupla{' '}
            <code className="font-mono">(informador, origen, destino)</code>.
            {conteoCustom > 0 && (
              <>
                {' '}
                Hay <b>{conteoCustom}</b> default{conteoCustom !== 1 ? 's' : ''} custom
                seteado{conteoCustom !== 1 ? 's' : ''}.
              </>
            )}
            {!puedeEditar && (
              <>
                {' '}
                Solo <b>Administrador</b> puede editar los defaults.
              </>
            )}
          </AlertDescription>
        </Alert>

        {/* Filtros */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
          <div className="md:col-span-2">
            <Label className="mb-1 block text-xs">Buscar</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
              <Input
                className="pl-9"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Texto del evento o ID"
              />
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-xs">Informador</Label>
            <Select value={filtroInformador} onValueChange={setFiltroInformador}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {TIPOS_ACTOR.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs">Origen</Label>
            <Select value={filtroOrigen} onValueChange={setFiltroOrigen}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {TIPOS_ACTOR.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-xs">Destino</Label>
            <Select value={filtroDestino} onValueChange={setFiltroDestino}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="__null">— (terminal)</SelectItem>
                {TIPOS_ACTOR.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <input
            id="solo-defaults"
            type="checkbox"
            checked={soloDefaults}
            onChange={(e) => setSoloDefaults(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300"
          />
          <label htmlFor="solo-defaults" className="cursor-pointer text-gray-600">
            Solo defaults vigentes (custom o hardcoded)
          </label>
          <span className="ml-auto text-[11px] text-gray-600">
            {filtrados.length} de {eventos.length} eventos
          </span>
        </div>

        {/* Tabla */}
        <div className="overflow-x-auto rounded border border-gray-100">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">ID</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Informador</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Destino</TableHead>
                <TableHead>Default</TableHead>
                {puedeEditar && <TableHead className="text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={puedeEditar ? 7 : 6} className="py-8 text-center text-xs text-gray-600">
                    Sin eventos para estos filtros.
                  </TableCell>
                </TableRow>
              ) : (
                filtrados.map((e) => {
                  const clave = eventoAnmatDefaultKey(e.informador, e.origen, e.destino);
                  const customId = defaults[clave];
                  const esCustom = customId === e.id;
                  const esDefaultActual = esEventoDefaultActual(e, defaults);
                  return (
                    <EventoRow
                      key={e.id}
                      evento={e}
                      esDefaultActual={esDefaultActual}
                      esCustom={esCustom}
                      tuplaTieneCustom={customId !== undefined}
                      puedeEditar={puedeEditar}
                      onSetDefault={() => handleSetDefault(e)}
                      onRemoveDefault={() => handleRemoveDefault(e)}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

interface EventoRowProps {
  evento: EventoAnmat;
  esDefaultActual: boolean;
  esCustom: boolean;
  tuplaTieneCustom: boolean;
  puedeEditar: boolean;
  onSetDefault: () => void;
  onRemoveDefault: () => void;
}

function EventoRow({
  evento: e,
  esDefaultActual,
  esCustom,
  tuplaTieneCustom,
  puedeEditar,
  onSetDefault,
  onRemoveDefault,
}: EventoRowProps) {
  // tipoActor helper para mostrar abreviados (LABORATORIO → "LAB")
  const abrev = (a: TipoActor | null | undefined): string => {
    if (!a) return '—';
    return a;
  };

  return (
    <TableRow className={cn(esDefaultActual && 'bg-amber-50/50')}>
      <TableCell className="font-mono text-xs">#{e.id}</TableCell>
      <TableCell className="text-xs">{e.evento}</TableCell>
      <TableCell className="text-[11px]">{abrev(e.informador)}</TableCell>
      <TableCell className="text-[11px]">{abrev(e.origen)}</TableCell>
      <TableCell className="text-[11px]">{abrev(e.destino) === '—' ? '— (terminal)' : abrev(e.destino)}</TableCell>
      <TableCell>
        {esDefaultActual ? (
          <span
            className={cn('badge', esCustom ? 'badge-purple' : 'badge-yellow')}
            title={esCustom ? 'Default custom seteado para esta tupla' : 'Default hardcoded del sistema'}
          >
            <Star className="mr-1 inline h-2.5 w-2.5" />
            {esCustom ? 'Custom' : 'Default'}
          </span>
        ) : tuplaTieneCustom ? (
          <span className="text-[11px] text-gray-600" title="Otra fila de esta tupla es el default custom">
            ·
          </span>
        ) : null}
      </TableCell>
      {puedeEditar && (
        <TableCell className="space-x-1 whitespace-nowrap text-right">
          {!esDefaultActual && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onSetDefault}
              className="h-7 text-[11px] text-everex-azul hover:bg-everex-azul-bg"
              title="Setear este evento como default para esta tupla"
            >
              <Star className="mr-1 h-3 w-3" /> Marcar default
            </Button>
          )}
          {esCustom && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRemoveDefault}
              className="h-7 text-[11px] text-amber-700 hover:bg-amber-50"
              title="Quitar el custom y volver al default hardcoded del sistema"
            >
              <StarOff className="mr-1 h-3 w-3" /> Quitar custom
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
